import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Award, Shield, Zap } from 'lucide-react';
import { companyInfo, images } from '../mock/mock';

const About = () => {
  const highlights = [
    { title: 'Since 2006', label: `${companyInfo.yearsInBusiness} Years`, icon: Award },
    { title: 'Our Focus', label: 'IT Excellence', icon: Zap },
    { title: 'Our Mission', label: 'Measurable Outcomes', icon: Shield }
  ];

  return (
    <section id="about" className="section-padding section-dark">
      <div className="container-x grid lg:grid-cols-2 gap-14 items-center relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10">
            <img src={images.about} alt="DC10G Datacenter Infrastructure" className="w-full h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/90 via-slate-900/40 to-transparent" />

            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center"><Award size={16} /></div>
                <span className="text-xs font-semibold uppercase tracking-widest text-cyan-300">Tier III Certified</span>
              </div>
              <h4 className="text-2xl font-bold">State-of-the-art infrastructure</h4>
            </div>
          </div>

          <motion.div
            initial={{ scale: 0, rotate: -20 }} whileInView={{ scale: 1, rotate: 0 }} viewport={{ once: true }} transition={{ delay: 0.3, type: 'spring' }}
            className="absolute -top-6 -right-6 w-32 h-32 rounded-full glass-strong border-4 border-cyan-400/60 flex flex-col items-center justify-center text-center shadow-2xl shadow-cyan-500/30"
          >
            <div className="text-3xl font-black gradient-text">18+</div>
            <div className="text-[9px] font-bold text-slate-300 uppercase tracking-widest mt-0.5">Years</div>
            <div className="text-[9px] text-slate-400">Excellence</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
            className="absolute -bottom-8 -left-6 glass-strong rounded-2xl shadow-2xl p-4 border border-cyan-400/30"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
                <Shield className="text-white" size={20} />
              </div>
              <div>
                <div className="text-lg font-black text-white">500+</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest">Enterprise Clients</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
        >
          <div className="eyebrow mb-4"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> Trusted Partner · About Our Company</div>
          <h2 className="section-title">Scalable performance for <span className="gradient-text">your web project.</span></h2>
          <p className="mt-6 text-slate-300 leading-relaxed">{companyInfo.description}</p>

          <div className="mt-8 grid grid-cols-3 gap-4">
            {highlights.map((h, i) => (
              <motion.div
                key={h.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 + i * 0.1 }}
                className="p-4 rounded-xl glass hover:border-cyan-400/40 transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center mb-2">
                  <h.icon size={16} className="text-cyan-300" />
                </div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">{h.title}</div>
                <div className="font-bold text-white text-sm">{h.label}</div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            {['End-to-end IT Solutions', 'Enterprise-grade infrastructure', 'Global service reach', 'Measurable outcomes'].map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm text-slate-300">
                <CheckCircle2 size={16} className="text-cyan-400" /> {t}
              </div>
            ))}
          </div>

          <a href="#services" className="btn-primary mt-8">
            Discover Our Services <ArrowRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
